/**
 * useOrders Hook
 *
 * Custom hook for fetching user orders from Firestore with real-time updates.
 * Supports filtering by service type.
 *
 * CRITICAL CONSTRAINTS:
 * - TypeScript strict mode (NO 'any' types)
 * - Real-time listener via onSnapshot
 * - User-scoped queries only
 * - NO localStorage
 */

import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot, Query, DocumentData } from 'firebase/firestore';
import { db } from '@/firebaseConfig';
import { OrderDocument, ServiceType } from '@/shared/types';

interface UseOrdersOptions {
  serviceType?: ServiceType | null;
  limit?: number;
}

interface UseOrdersReturn {
  orders: OrderDocument[];
  loading: boolean;
  error: string | null;
}

/**
 * Custom hook to fetch user orders with real-time updates
 *
 * @param userId - User ID to fetch orders for
 * @param options - Optional filtering and limit options
 * @returns Orders, loading state, and error
 */
export function useOrders(userId: string | undefined, options?: UseOrdersOptions): UseOrdersReturn {
  const [orders, setOrders] = useState<OrderDocument[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setOrders([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Build query
      let q: Query<DocumentData> = query(
        collection(db, 'orders'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      // Add service type filter if specified
      if (options?.serviceType) {
        q = query(
          collection(db, 'orders'),
          where('userId', '==', userId),
          where('serviceType', '==', options.serviceType),
          orderBy('createdAt', 'desc')
        );
      }

      // Set up real-time listener
      const unsubscribe = onSnapshot(
        q,
        (querySnapshot) => {
          const ordersData: OrderDocument[] = [];
          querySnapshot.forEach((doc) => {
            const data = doc.data();

            // Sanitize attempts to ensure strict type compliance (undefined -> null)
            const attempts = (data.attempts || []).map((attempt: DocumentData) => ({
              ...attempt,
              resultUrl: attempt.resultUrl ?? null,
              errorMessage: attempt.errorMessage ?? null,
            }));

            ordersData.push({
              ...data,
              orderId: doc.id,
              // Sanitize root level fields
              currentResultUrl: data.currentResultUrl ?? null,
              currentResultPath: data.currentResultPath ?? null,
              attempts: attempts,
            } as OrderDocument);
          });

          // Apply limit if specified
          if (options?.limit) {
            setOrders(ordersData.slice(0, options.limit));
          } else {
            setOrders(ordersData);
          }

          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error('Error fetching orders:', err);
          setError('Failed to load orders');
          setLoading(false);
        }
      );

      return unsubscribe;
    } catch (err) {
      console.error('Error setting up orders listener:', err);
      setError('Failed to load orders');
      setLoading(false);
    }
  }, [userId, options?.serviceType, options?.limit]);

  return { orders, loading, error };
}
